/**
 * The review-date surface — §7.2, "the highest-leverage pair in the entire
 * build".
 *
 * "Confluence did not fail because of its editor — it failed because nothing
 * forced a document to be re-read, and stale documents poison trust in the whole
 * corpus." This file is the reader's half of that loop; `apps/api`'s
 * `documents/review.ts` and the Monday digest are the other half.
 *
 * Two rules constrain everything here, and both are easy to violate while trying
 * to be helpful:
 *
 * 1. **A document past its review date must be visibly past it.** Not a
 *    tooltip, not a subtle grey date — a banner above the body, naming the date
 *    and how long ago it was.
 * 2. **`needs_review` stays fully visible and fully readable.** §7.2:
 *    "Unpublishing stale documents is worse than stale documents." So nothing in
 *    this file lowers the body's contrast, collapses it, hides it behind a
 *    "show anyway" button, or blocks it with an interstitial. The banner sits
 *    *beside* the document, never in front of it. If a future change makes the
 *    body harder to read because a date passed, that change is the bug.
 *
 * The way out of `needs_review` is the owner reading the document and setting a
 * new date — `statusAfterReviewDateChange` in the API returns it to `published`
 * the moment `review_by` moves into the future. That is why the banner carries
 * the date form rather than only a complaint.
 */

import { useState } from 'react';
import { Form } from 'react-aria-components';
import { useUpdateDocument } from '../../api/queries.ts';
import type { DocumentType, DocumentView } from '../../api/types.ts';
import { REVIEWABLE_DOCUMENT_TYPES } from '../../api/types.ts';
import { formatDay, parseCalendarDate } from '../../components/format.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { TextField } from '../../components/ui/field.tsx';

/** The API's own shape for `document.review_by`, mirrored so a 400 is a hint. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isReviewable(type: DocumentType): boolean {
  return REVIEWABLE_DOCUMENT_TYPES.includes(type);
}

/**
 * A `date` column, rendered as the day it names.
 *
 * The rule it obeys — a calendar day is built as a *local* midnight, never
 * parsed as an instant — now lives in `components/format.ts` beside the
 * timestamp formatters it is easy to reach for by mistake. This name stays
 * because it is what every review surface calls it.
 */
export const formatCalendarDate = formatDay;

/**
 * Whole days from today to the review date. Negative is overdue.
 *
 * Both sides are local midnights so the answer is a count of calendar days
 * rather than a count of 24-hour periods, which differ across a DST boundary and
 * would round "due tomorrow" into "due today" twice a year.
 */
export function reviewDayDelta(reviewBy: string, now = new Date()): number | null {
  const due = parseCalendarDate(reviewBy);
  if (!due) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/** "34 days ago", "today", "in 12 days" — the phrase a person reads first. */
export function reviewPhrase(reviewBy: string, now = new Date()): string {
  const delta = reviewDayDelta(reviewBy, now);
  if (delta === null) return reviewBy;
  if (delta === 0) return 'today';
  if (delta < 0) {
    const days = Math.abs(delta);
    return days === 1 ? 'yesterday' : `${days} days ago`;
  }
  return delta === 1 ? 'tomorrow' : `in ${delta} days`;
}

/**
 * The review date as one line, for a definition list or a table cell.
 *
 * `reviewState` comes from the API and is the *calendar's* answer; `status` is
 * the lifecycle's. Between the date passing and the 07:00 sweep running they
 * disagree, and this renders the calendar's — a person looking at a document at
 * 09:00 the morning it lapsed should not be told it is fine because a cron job
 * has not run.
 */
export function ReviewDate({ document }: { document: DocumentView }) {
  if (!document.reviewBy) {
    return isReviewable(document.type) ? (
      <span className="text-tertiary">
        No review date. Nothing will ever ask for this to be re-read.
      </span>
    ) : (
      <span className="text-tertiary">
        Not on a review cycle — §7.2 confines <span className="font-mono">review_by</span> to
        playbooks and references.
      </span>
    );
  }

  const lapsed = document.reviewState === 'lapsed';
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className={lapsed ? 'font-semibold text-warning-primary' : undefined}>
        {formatCalendarDate(document.reviewBy)}
      </span>
      <Badge tone={lapsed ? 'warning' : 'outline'}>Due {reviewPhrase(document.reviewBy)}</Badge>
    </span>
  );
}

/**
 * The banner. This is the whole feature, and it is deliberately loud.
 *
 * Rendered whenever the calendar says the date has passed **or** the sweep has
 * already moved the status, so neither half of the disagreement can hide it.
 */
export function ReviewBanner({ document, canEdit }: { document: DocumentView; canEdit: boolean }) {
  const [editing, setEditing] = useState(false);
  const lapsed = document.reviewState === 'lapsed';
  const flagged = document.status === 'needs_review';

  if (!lapsed && !flagged) return null;

  const reviewBy = document.reviewBy;

  return (
    <>
      <Alert
        tone="warning"
        title={
          reviewBy
            ? `Past its review date — due ${formatCalendarDate(reviewBy)}, ${reviewPhrase(reviewBy)}`
            : 'Flagged for review'
        }
      >
        <div className="flex flex-col gap-3">
          <p>
            This is still the document everyone reads, and everyone who could reach it still can.
            §7.2 is explicit that unpublishing a stale document is worse than the stale document —
            so nothing here is hidden, greyed out, or gated. What it needs is someone to read it.
          </p>
          {lapsed && !flagged ? (
            <p className="text-xs opacity-80">
              The status still says <span className="font-mono">{document.status}</span>: the daily
              07:00 sweep has not run since the date passed. The calendar is the one to believe.
            </p>
          ) : null}
          {flagged && !lapsed ? (
            <p className="text-xs opacity-80">
              The status says <span className="font-mono">needs_review</span> from an earlier lapse.
              Saving the new date is what returns it to{' '}
              <span className="font-mono">
                {document.publishedVersionId ? 'published' : 'draft'}
              </span>
              .
            </p>
          ) : null}
          {canEdit ? (
            <div>
              <Button size="sm" variant="secondary" onPress={() => setEditing(true)}>
                {reviewBy ? 'Set a new review date' : 'Set a review date'}
              </Button>
            </div>
          ) : (
            <p className="text-xs opacity-80">
              Its owner is the one being nagged (§7.2 nags the owner, singular).
            </p>
          )}
        </div>
      </Alert>

      <ReviewDateDialog document={document} isOpen={editing} onOpenChange={setEditing} />
    </>
  );
}

export function ReviewDateDialog({
  document,
  isOpen,
  onOpenChange,
}: {
  document: DocumentView;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog
      title="Review date"
      description={`${document.title} — §7.2 applies review dates to playbooks and references.`}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {(close) => <ReviewDateForm document={document} onClose={close} />}
    </Dialog>
  );
}

/** The intervals people actually pick. Nothing turns on the exact set. */
const INTERVALS: ReadonlyArray<{ label: string; months: number }> = [
  { label: '3 months', months: 3 },
  { label: '6 months', months: 6 },
  { label: '1 year', months: 12 },
];

function addMonths(months: number, from = new Date()): string {
  const date = new Date(from.getFullYear(), from.getMonth() + months, from.getDate());
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Setting the date.
 *
 * The quick buttons exist because the interval is the decision and the exact day
 * almost never is — asking someone to type `2027-02-14` to mean "in six months"
 * is how a field gets left empty. Typing one is still allowed; the API takes
 * `YYYY-MM-DD` and this refuses to send anything else rather than turning a
 * typo into a 400 with a schema message.
 */
export function ReviewDateForm({
  document,
  onClose,
}: {
  document: DocumentView;
  onClose: () => void;
}) {
  const update = useUpdateDocument(document.nodeId);
  const [value, setValue] = useState(document.reviewBy ?? addMonths(6));

  const trimmed = value.trim();
  const wellFormed = CALENDAR_DATE.test(trimmed);
  const delta = wellFormed ? reviewDayDelta(trimmed) : null;
  const inThePast = delta !== null && delta < 0;

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        if (!wellFormed) return;
        update.mutate({ reviewBy: trimmed }, { onSuccess: onClose });
      }}
    >
      <DialogBody>
        <TextField
          label="Next review"
          value={value}
          onChange={setValue}
          isRequired
          autoFocus
          placeholder="YYYY-MM-DD"
          description="A calendar day, as the column stores it."
          {...(wellFormed ? {} : { errorMessage: 'Use YYYY-MM-DD.' })}
        />
        <div className="flex flex-wrap gap-2">
          {INTERVALS.map((interval) => (
            <Button
              key={interval.label}
              size="sm"
              variant="secondary"
              onPress={() => setValue(addMonths(interval.months))}
            >
              {interval.label}
            </Button>
          ))}
        </div>

        {inThePast ? (
          <Alert tone="warning" title="That date is already past">
            Saving it leaves the document in <span className="font-mono">needs_review</span> — the
            status only recovers when the date is in the future.
          </Alert>
        ) : null}

        {document.reviewBy ? (
          <p className="text-sm text-tertiary">
            Currently {formatCalendarDate(document.reviewBy)} ({reviewPhrase(document.reviewBy)}).
          </p>
        ) : null}

        {update.isError ? <ErrorState error={update.error} /> : null}
      </DialogBody>
      <DialogFooter>
        {document.reviewBy ? (
          <Button
            variant="tertiary"
            className="mr-auto text-error-primary"
            isDisabled={update.isPending}
            onPress={() => update.mutate({ reviewBy: null }, { onSuccess: onClose })}
          >
            Clear the date
          </Button>
        ) : null}
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={update.isPending}
          isDisabled={!wellFormed}
        >
          Save review date
        </Button>
      </DialogFooter>
    </Form>
  );
}
